<template>
  <div class="challenges-page">
    <!-- 顶部：挑战列表 + 我的进行中 合并 -->
    <PageHeader
      title="挑战列表"
      description="选择挑战，完成学习目标"
    >
      <template
        v-if="ongoingAttempts.length > 0"
        #extra
      >
        <SectionCard
          class="ongoing-section"
          title="我的进行中"
        >
          <template #actions>
            <el-tag
              type="info"
              size="small"
            >
              进行中 {{ ongoingAttempts.length }}
            </el-tag>
          </template>
          <div class="ongoing-grid">
            <el-card
              v-for="attempt in ongoingAttempts"
              :key="attempt.id"
              class="ongoing-card"
              shadow="hover"
              @click="goToChallengeDetail(attempt.challengeId)"
              v-loading="loadingOngoing"
            >
              <div class="ongoing-content">
                <div
                  class="ongoing-info"
                  @click.stop="goToChallengeDetail(attempt.challengeId)"
                >
                  <h4>{{ getChallengeTitle(attempt.challengeId) }}</h4>
                  <div class="ongoing-meta">
                    <el-tag
                      type="info"
                      size="small"
                    >
                      进行中
                    </el-tag>
                    <span
                      v-if="attempt.bestScore"
                      class="score"
                    >
                      最佳得分: {{ attempt.bestScore }}
                    </span>
                  </div>
                </div>
                <div class="ongoing-actions">
                  <el-button
                    type="primary"
                    size="small"
                    @click.stop="goToAttemptPage(attempt.id)"
                  >
                    继续挑战
                  </el-button>
                </div>
              </div>
            </el-card>
          </div>
        </SectionCard>
      </template>
    </PageHeader>

    <!-- 中部：筛选 + 全部挑战 合并 -->
    <SectionCard
      class="list-section"
      title="全部挑战"
    >
      <!-- 筛选区 -->
      <el-form
        :inline="true"
        class="filter-form"
      >
        <el-form-item label="难度">
          <el-input-number 
            v-model="filters.level" 
            :min="0" 
            :max="30" 
            class="filter-number"
          />
        </el-form-item>
        <el-form-item label="分类">
          <el-select
            v-model="filters.category"
            placeholder="全部"
            clearable
            filterable
            class="filter-select"
          >
            <el-option
              label="全部"
              :value="''"
            />
            <el-option
              label="前端项目"
              value="前端项目"
            />
            <el-option
              label="后端项目"
              value="后端项目"
            />
            <el-option
              label="全栈项目"
              value="全栈项目"
            />
            <el-option
              label="算法题"
              value="算法题"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            @click="loadChallenges"
          >
            查询
          </el-button>
          <el-button
            type="primary"
            @click="resetFilters"
          >
            清空筛选
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 全部挑战列表 -->
      <div
        v-loading="loading && challenges.length === 0"
        class="challenges-grid"
      >
        <el-card
          v-for="challenge in challenges"
          :key="challenge.id"
          class="challenge-card"
          shadow="never"
          @click="goToDetail(challenge.id)"
        >
          <div class="challenge-inner">
            <div class="challenge-cover">
              <el-image
                v-if="challenge.coverImageUrl"
                :src="getCoverThumbSrc(challenge)"
                :preview-src-list="getPreviewSrcList(challenge)"
                fit="cover"
                class="cover-image"
                lazy
                preview-teleported
                :hide-on-click-modal="true"
                :z-index="3000"
              />
              <div
                v-else
                class="cover-placeholder"
              >
                <el-icon :size="48">
                  <Trophy />
                </el-icon>
              </div>
              <el-tag
                :type="getLevelType(challenge.level)"
                class="level-badge"
              >
                {{ getLevelText(challenge.level) }}
              </el-tag>
            </div>
            <div class="challenge-content">
              <h3 class="challenge-title">
                {{ challenge.title }}
              </h3>
              <div
                v-if="challenge.category"
                class="challenge-category"
              >
                <el-tag
                  type="info"
                  size="small"
                  effect="plain"
                >
                  {{ challenge.category }}
                </el-tag>
              </div>
              <div
                class="challenge-tags"
                v-if="challenge.tags && challenge.tags.length > 0"
              >
                <el-tag
                  v-for="tag in challenge.tags"
                  :key="tag.id"
                  size="small"
                  class="tag-item"
                  effect="plain"
                >
                  {{ tag.name }}
                </el-tag>
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <el-pagination
        v-model:current-page="pagination.current"
        v-model:page-size="pagination.size"
        :total="pagination.total"
        @current-change="loadChallenges"
        @size-change="loadChallenges"
        layout="total, sizes, prev, pager, next"
      />
    </SectionCard>
  </div>
</template>

<script setup>
import { ref, onMounted, onActivated, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Trophy } from '@element-plus/icons-vue'
import request from '@/utils/request'
import { takePrefetchedChallenges } from '@/utils/challengesPrefetch'
import {
  getChallengesListCache,
  setChallengesListCache,
  getOngoingCache,
  setOngoingCache
} from '@/utils/challengesPageCache'
import { getThumbUrl } from '@/utils/imageThumbCache'
import { getImageUrl } from '@/utils/imageUrl'
import PageHeader from '@/components/ui/PageHeader.vue'
import SectionCard from '@/components/ui/SectionCard.vue'

const router = useRouter()

const loading = ref(false)
const challenges = ref([])
const ongoingAttempts = ref([])
const challengeTitles = ref({}) // 缓存挑战标题
const thumbSrcMap = ref({}) // challengeId -> thumb src
const filters = ref({
  // 0 表示“全部难度”
  level: 0,
  category: ''
})
const pagination = ref({
  current: 1,
  size: 10,
  total: 0
})

const resetFilters = () => {
  // 难度和分类都恢复为“全部”
  filters.value.level = 0
  filters.value.category = ''
  // 回到第一页
  pagination.value.current = 1
  // 重新加载数据
  loadChallenges()
}

const getLevelType = (level) => {
  if (typeof level !== 'number') return ''
  // 根据难度范围设置颜色：1-10=success, 11-20=warning, 21-30=danger
  if (level <= 10) return 'success'
  if (level <= 20) return 'warning'
  return 'danger'
}

const getLevelText = (level) => {
  if (typeof level === 'number') {
    return `level:${level}`
  }
  return level || '未知'
}

/**
 * @param {{ silent?: boolean }} opts silent：后台刷新，有缓存时不挡全屏
 */
const loadChallenges = async (opts = {}) => {
  const silent = !!opts.silent
  const cur = pagination.value.current
  const sz = pagination.value.size
  const lv = filters.value.level
  const cat = filters.value.category ?? ''
  if (!silent) {
    const cached = getChallengesListCache(cur, sz, lv, cat)
    if (cached) {
      challenges.value = cached.records
      pagination.value.total = cached.total
      loading.value = false
      loadChallenges({ silent: true })
      return
    }
    loading.value = true
  }
  try {
    const params = {
      current: cur,
      size: sz,
      level: lv || undefined,
      category: cat || undefined
    }
    if (!params.level) delete params.level
    if (!params.category) delete params.category
    const data = await request.get('/challenges', { params })
    if (
      pagination.value.current !== cur ||
      pagination.value.size !== sz ||
      filters.value.level !== lv ||
      (filters.value.category ?? '') !== cat
    ) {
      return
    }
    challenges.value = data.records
    pagination.value.total = data.total
    setChallengesListCache(cur, sz, lv, cat, data.records, data.total)
  } catch (error) {
    if (!silent) ElMessage.error('加载失败')
  } finally {
    if (!silent) loading.value = false
  }
}

const goToDetail = (id) => {
  router.push(`/challenges/${id}`)
}

const goToChallengeDetail = (challengeId) => {
  // 点击卡片跳转到挑战详情页，显示任务简介
  router.push(`/challenges/${challengeId}`)
}

const goToAttemptPage = (attemptId) => {
  // 点击"继续挑战"按钮跳转到尝试页面
  router.push(`/attempts/${attemptId}`)
}

const getCoverThumbSrc = (challenge) => {
  if (!challenge?.coverImageUrl) return ''
  return thumbSrcMap.value[challenge.id] || getImageUrl(challenge.coverImageUrl)
}

// 预览列表用稳定引用，避免点击放大时预览层因引用变化重绘闪烁
const previewListCache = Object.create(null)
const getPreviewSrcList = (challenge) => {
  if (!challenge?.coverImageUrl) return []
  const url = getImageUrl(challenge.coverImageUrl)
  const cached = previewListCache[challenge.id]
  if (cached && cached[0] === url) return cached
  previewListCache[challenge.id] = [url]
  return previewListCache[challenge.id]
}

const loadingOngoing = ref(false)

/**
 * @param {{ silent?: boolean }} opts
 */
const loadOngoingAttempts = async (opts = {}) => {
  const silent = !!opts.silent
  if (!silent) loadingOngoing.value = true
  try {
    const response = await request.get('/attempts/my/ongoing')
    let attempts = []
    if (Array.isArray(response)) {
      attempts = response
    } else if (response && Array.isArray(response.data)) {
      attempts = response.data
    } else if (response && Array.isArray(response.records)) {
      attempts = response.records
    }
    ongoingAttempts.value = attempts

    if (ongoingAttempts.value.length > 0) {
      const titlePromises = ongoingAttempts.value.map(async (attempt) => {
        if (!challengeTitles.value[attempt.challengeId]) {
          try {
            const challenge = await request.get(`/challenges/${attempt.challengeId}`)
            challengeTitles.value[attempt.challengeId] =
              challenge.title || challenge.challenge?.title || '未知挑战'
          } catch (error) {
            challengeTitles.value[attempt.challengeId] = '未知挑战'
          }
        }
      })
      await Promise.all(titlePromises)
    }
    setOngoingCache(ongoingAttempts.value, challengeTitles.value)
  } catch (error) {
    console.error('加载进行中的挑战失败:', error)
    if (!silent) {
      ElMessage.error('加载进行中的挑战失败: ' + (error.message || '未知错误'))
      ongoingAttempts.value = []
    }
  } finally {
    if (!silent) loadingOngoing.value = false
  }
}

const getChallengeTitle = (challengeId) => {
  const title = challengeTitles.value[challengeId]
  if (title) {
    return title
  }
  // 如果还没有加载，返回加载中
  if (loadingOngoing.value) {
    return '加载中...'
  }
  // 如果加载完成但没有标题，返回未知
  return '未知挑战'
}

onMounted(async () => {
  const prefetched = await takePrefetchedChallenges()
  const cur = pagination.value.current
  const sz = pagination.value.size
  const lv = filters.value.level
  const cat = filters.value.category ?? ''
  if (prefetched && Array.isArray(prefetched.records)) {
    challenges.value = prefetched.records
    pagination.value.total = prefetched.total ?? 0
    setChallengesListCache(cur, sz, lv, cat, prefetched.records, prefetched.total ?? 0)
    loading.value = false
    loadChallenges({ silent: true })
  } else {
    const listCached = getChallengesListCache(cur, sz, lv, cat)
    const ongoingCached = getOngoingCache()
    if (listCached) {
      challenges.value = listCached.records
      pagination.value.total = listCached.total
      loading.value = false
      loadChallenges({ silent: true })
    } else {
      await loadChallenges()
    }
    if (ongoingCached?.ongoingAttempts) {
      ongoingAttempts.value = ongoingCached.ongoingAttempts
      challengeTitles.value = { ...challengeTitles.value, ...ongoingCached.challengeTitles }
      loadOngoingAttempts({ silent: true })
    } else {
      await loadOngoingAttempts()
    }
  }
})

onActivated(() => {
  loadChallenges({ silent: true })
  loadOngoingAttempts({ silent: true })
})

// challenges 列表变化时，异步生成/读取缩略图并缓存到本地
watch(challenges, async (list) => {
  if (!Array.isArray(list) || list.length === 0) return
  const entries = await Promise.all(
    list
      .filter(c => c && c.id && c.coverImageUrl)
      .map(async (c) => {
        const full = getImageUrl(c.coverImageUrl)
        const thumb = await getThumbUrl(full, { width: 360, height: 240 })
        return [c.id, thumb]
      })
  )
  const next = { ...thumbSrcMap.value }
  for (const [id, src] of entries) next[id] = src
  thumbSrcMap.value = next
}, { immediate: true })
</script>

<style scoped lang="scss">
.challenges-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-xxl);
  position: relative;
  z-index: 10;
  pointer-events: auto;
}

/* 深色主题下：在背景图上加一块深色“挑战面板”（遮罩不参与点击） */
:global(:root[data-theme='dark']) .challenges-page::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    rgba(15, 23, 42, 0.96),
    rgba(15, 23, 42, 0.9)
  );
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
  pointer-events: none !important;
  z-index: -1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
}

.section-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);

  .el-icon {
    color: var(--color-primary);
  }
}

.list-section {
  margin-top: var(--spacing-lg);
  pointer-events: auto;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  pointer-events: auto;
}

.filter-form :deep(.el-button),
.filter-form :deep(.el-input-number),
.filter-form :deep(.el-select) {
  cursor: pointer;
}

.filter-card {
  margin-bottom: var(--spacing-xl);
}

.filter-select {
  width: 180px;
}

.filter-number {
  width: 150px;
}

.challenges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-md);
}

.challenge-card {
  cursor: pointer;
  border-radius: var(--radius-lg);
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0;
  transition: transform var(--transition-fast) var(--ease-out);

  .challenge-inner {
    background: var(--color-bg-card);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition:
      transform var(--transition-fast) var(--ease-out),
      box-shadow var(--transition-fast) var(--ease-out),
      border-color var(--transition-fast) var(--ease-out);
  }

  &:hover .challenge-inner {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: var(--color-primary-100);
  }
}

.challenge-cover {
  position: relative;
  width: 100%;
  height: 190px;
  margin-bottom: var(--spacing-md);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-bg-secondary);
  
  .cover-image {
    width: 100%;
    height: 100%;
  }
  
  .cover-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
    background: linear-gradient(
      135deg,
      var(--color-primary-50) 0%,
      var(--color-primary-100) 100%
    );
  }
  
  .level-badge {
    position: absolute;
    top: var(--spacing-sm);
    right: var(--spacing-sm);
  }
}

/* 亮色主题下：改为浅色高阶面板语言，整页矩形无圆角，并至少占满一屏高度 */
:global(body[data-theme='light']) .challenges-page {
  padding: var(--spacing-xl) 0 var(--spacing-xxl);
  border-radius: 0;
  min-height: calc(100vh - 72px); /* 顶部导航 72px，高度至少铺满导航下方整屏 */
}

/* 亮色主题：顶部无装饰层，保持简洁 GitHub 风格 */
:global(body[data-theme='light']) .challenges-page::before {
  display: none;
}

:global(body[data-theme='light']) .challenges-page :deep(.page-header),
:global(body[data-theme='light']) .list-section.section-card,
:global(body[data-theme='light']) .ongoing-section.section-card {
  border-radius: var(--radius-lg);
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
  backdrop-filter: none;
}

/* 亮色主题：去掉卡片上的双圆径向渐变，避免椭圆块感 */
:global(body[data-theme='light']) .list-section.section-card::after,
:global(body[data-theme='light']) .ongoing-section.section-card::after {
  display: none;
}

:global(body[data-theme='light']) .list-section :deep(.section-header),
:global(body[data-theme='light']) .list-section :deep(.section-content),
:global(body[data-theme='light']) .ongoing-section :deep(.section-header),
:global(body[data-theme='light']) .ongoing-section :deep(.section-content) {
  position: relative;
  z-index: 1;
}

:global(body[data-theme='light']) .filter-form {
  padding: 18px 20px;
  margin: var(--spacing-sm) 0 var(--spacing-lg);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-sm);
}

:global(body[data-theme='light']) .filter-form :deep(.el-form-item) {
  margin-bottom: 0;
}

:global(body[data-theme='light']) .filter-form :deep(.el-input__wrapper),
:global(body[data-theme='light']) .filter-form :deep(.el-select__wrapper) {
  background: var(--color-bg-card);
  box-shadow: 0 0 0 1px var(--color-border) inset;
}

:global(body[data-theme='light']) .filter-form :deep(.el-input__wrapper:hover),
:global(body[data-theme='light']) .filter-form :deep(.el-select__wrapper:hover) {
  box-shadow: 0 0 0 1px var(--color-border-dark) inset;
}

:global(body[data-theme='light']) .filter-form :deep(.el-input__wrapper.is-focus),
:global(body[data-theme='light']) .filter-form :deep(.el-select__wrapper.is-focused) {
  box-shadow: var(--shadow-focus);
}

:global(body[data-theme='light']) .challenges-grid {
  gap: 22px;
  margin-top: var(--spacing-lg);
}

.challenge-content {
  .challenge-title {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .challenge-tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-xs);
    
    .tag-item {
      margin: 0;
    }
  }

  .challenge-category {
    margin-bottom: 6px;
  }
}

:global(body[data-theme='light']) .challenge-card {
  background: transparent;
  border: none;
  box-shadow: none;
}

:global(body[data-theme='light']) .challenge-card::before {
  display: none;
}

:global(body[data-theme='light']) .challenge-card:hover {
  transform: none;
  box-shadow: none;
}

:global(body[data-theme='light']) .challenge-card :deep(.el-card__body) {
  padding: 0;
  background: transparent;
}

/* 亮色主题：挑战卡片 — 白底 + 细边框，GitHub 风格 */
:global(body[data-theme='light']) .challenge-card .challenge-inner {
  position: relative;
  min-height: 100%;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-card);
}

:global(body[data-theme='light']) .challenge-card .challenge-inner::after {
  display: none;
}

:global(body[data-theme='light']) .challenge-cover {
  height: 210px;
  margin-bottom: 0;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-bg-secondary);
}

:global(body[data-theme='light']) .challenge-cover .cover-placeholder,
:global(body[data-theme='light']) .challenge-cover .el-image__error {
  background: var(--color-bg-secondary);
  color: var(--color-text-tertiary);
}

:global(body[data-theme='light']) .challenge-cover .level-badge {
  top: 14px;
  right: 14px;
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
  background: var(--color-bg-card);
}

:global(body[data-theme='light']) .challenge-content {
  padding: 18px 20px 20px;
}

:global(body[data-theme='light']) .challenge-content .challenge-title {
  margin-bottom: 10px;
  line-height: 1.35;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  min-height: calc(1.35em * 2);
  letter-spacing: 0.01em;
}

:global(body[data-theme='light']) .challenge-category .el-tag {
  border-radius: 999px;
  border: 1px solid var(--color-border-light);
  background: var(--color-primary-50);
  color: var(--color-primary);
  font-weight: 500;
}

:global(body[data-theme='light']) .challenge-tags .tag-item {
  border-radius: 999px;
  border: 1px solid var(--color-border-light);
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
}

:global(body[data-theme='light']) .challenge-card:hover .challenge-inner {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
  border-color: var(--color-border-dark);
}

:global(body[data-theme='light']) .ongoing-card {
  border: 1px solid var(--color-border);
  background: #ffffff !important;
  box-shadow: var(--shadow-card);
}

:global(body[data-theme='light']) .ongoing-card :deep(.el-card__body) {
  background-color: #ffffff !important;
  color: #24292f;
}

:global(body[data-theme='light']) .ongoing-card:hover {
  box-shadow: var(--shadow-hover);
}

.ongoing-section {
  margin-bottom: var(--spacing-xl);
}

.ongoing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--spacing-md);
}

.ongoing-card {
  cursor: pointer;
  transition:
    transform var(--transition-fast) var(--ease-out),
    box-shadow var(--transition-fast) var(--ease-out);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
}

.ongoing-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md);
}

.ongoing-info {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  
  h4 {
    margin: 0 0 var(--spacing-xs) 0;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .ongoing-meta {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    
    .score {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }
  }
}

.ongoing-actions {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .challenges-grid {
    grid-template-columns: 1fr;
  }

  .ongoing-grid {
    grid-template-columns: 1fr;
  }
}
</style>
